import React, { useEffect, useState } from 'react';
import { Tag, Empty, Skeleton, Tooltip, Alert } from 'antd';
import {
  WarningOutlined, FileExclamationOutlined, SafetyCertificateOutlined,
  ExclamationCircleOutlined, HistoryOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import bilateralesService from '../../../services/bilateralesService';
import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../../i18n/libelles';

/**
 * Chaque type d'insuffisance pointe vers l'onglet du DetailsSinistre qui
 * permet de la résoudre dans le process normal (continuité - on ne refait
 * pas un workflow parallèle dans le module bilatéral).
 */
const TYPE_META = avecLibelleTraduit(avecLibelleTraduit({
  PIECE_MANQUANTE: {
    color: 'red', icon: <FileExclamationOutlined />,
    tab: 'documents',
  },
  RC_NON_DEFINIE: {
    color: 'orange', icon: <WarningOutlined />,
    tab: 'victimes',
  },
  PV_ABSENT: {
    color: 'orange', icon: <WarningOutlined />,
    tab: 'pv',
  },
  GARANTIE_NON_CONFIRMEE: {
    color: 'orange', icon: <SafetyCertificateOutlined />,
    tab: 'infos',
  },
  LITIGE_EN_COURS: {
    color: 'volcano', icon: <ExclamationCircleOutlined />,
    tab: 'journal',
  },
  DEJA_DISCUTE: {
    color: 'gold', icon: <HistoryOutlined />,
    tab: 'bilateraux',
  },
}, 'bilaterales:insuffisances.types'), 'bilaterales:insuffisances.actions', { champ: 'cta' });

export default function InsuffisancesPanel({ sinistreTrackingId }) {
  const { t } = useTranslation('bilaterales');
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!sinistreTrackingId) return;
    bilateralesService.getInsuffisancesDuSinistre(sinistreTrackingId)
      .then(setItems)
      .catch(() => setItems([]));
  }, [sinistreTrackingId]);

  if (items === null) return <Skeleton active paragraph={{ rows: 2 }} />;
  if (!items.length) {
    return (
      <Alert
        type="success" showIcon
        message={t('insuffisances.dossierMur')}
      />
    );
  }

  const aPiecesManquantes = items.some((i) => i.type === 'PIECE_MANQUANTE');

  return (
    <div>
      {aPiecesManquantes && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 10 }}
          message={t('insuffisances.sinistreNonMur')}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it, i) => {
          const meta = TYPE_META[it.type] || { color: 'default', icon: null, label: it.type, cta: t('insuffisances.ouvrir'), tab: 'infos' };
          const sinistreUrl = `/sinistres/${sinistreTrackingId}?tab=${meta.tab}`;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6,
              background: '#fafafa',
            }}>
              <Tag color={meta.color} icon={meta.icon} style={{ minWidth: 160, margin: 0 }}>
                {meta.label}
              </Tag>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{it.libelle}</div>
                {it.details && (
                  <div style={{ color: '#6b7280', fontSize: 12 }}>{it.details}</div>
                )}
              </div>
              <Tooltip title={t('insuffisances.continuer')}>
                <a href={sinistreUrl} target="_blank" rel="noreferrer"
                  style={{
                    fontSize: 12, fontWeight: 600, color: '#1a6b3c',
                    whiteSpace: 'nowrap', textDecoration: 'none'
                  }}>
                  {meta.cta} <ArrowRightOutlined />
                </a>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
}
